/**
 * 搜索提供方解析与依赖注入（T12）。
 *
 * - 生产路径：从本地配置（localStorage key `t12.searchConfig`）读取 `ResolvedSearchConfig`，
 *   经 `src/core/search` 的注册表实例化 `SearchProvider`；本层只调用核心层，不重做检索逻辑。
 * - 测试路径：把 `SearchRunner` 作为依赖注入，直接返回 fake 结果，**不发起任何网络请求**。
 *
 * 注意：本文件不修改 `src/core/**` 契约；`SearchProviderKind` 等类型全部从核心层导入。
 */

import { SearchError, createSearchProvider } from '../../core/search';
import type { ResolvedSearchConfig, SearchProvider, SearchResult } from '../../core/search';

/** 本地搜索提供方配置的存储 key（明文密钥不入库，只存 `apiKeyRef`）。 */
export const SEARCH_CONFIG_STORAGE_KEY = 't12.searchConfig';

/** 一次检索的可注入入口：默认取「本地已配置的 provider」，测试注入 fake。 */
export type SearchRunner = (
  query: string,
  options?: { signal?: AbortSignal; limit?: number },
) => Promise<SearchResult[]>;

function storage(): Storage | undefined {
  try {
    return typeof globalThis.localStorage === 'undefined' ? undefined : globalThis.localStorage;
  } catch {
    return undefined;
  }
}

function isResolvedSearchConfig(value: unknown): value is ResolvedSearchConfig {
  if (value === null || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.id === 'string' &&
    typeof record.kind === 'string' &&
    typeof record.baseUrl === 'string' &&
    typeof record.apiKeyRef === 'string'
  );
}

/** 读取本地搜索配置；缺省 / 损坏 / 缺少必填字段时返回 null。 */
export function loadSearchConfig(): ResolvedSearchConfig | null {
  const store = storage();
  if (store === undefined) return null;
  let raw: string | null;
  try {
    raw = store.getItem(SEARCH_CONFIG_STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isResolvedSearchConfig(parsed)) return null;
    return {
      ...parsed,
      name: typeof parsed.name === 'string' && parsed.name !== '' ? parsed.name : parsed.kind,
      enabled: parsed.enabled ?? true,
    };
  } catch {
    return null;
  }
}

/** 覆盖写入本地搜索配置（`enabled=false` 时保留配置但不参与检索）。 */
export function saveSearchConfig(config: ResolvedSearchConfig): ResolvedSearchConfig {
  const normalized: ResolvedSearchConfig = {
    ...config,
    name: config.name === '' ? config.kind : config.name,
    enabled: config.enabled !== false,
  };
  try {
    storage()?.setItem(SEARCH_CONFIG_STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    // 存储不可用时忽略。
  }
  return normalized;
}

/** 清除本地搜索配置。 */
export function clearSearchConfig(): void {
  try {
    storage()?.removeItem(SEARCH_CONFIG_STORAGE_KEY);
  } catch {
    // 存储不可用时忽略。
  }
}

/**
 * 用本地已配置的 provider 实例化搜索适配器；未配置 / 未启用 / kind 未注册时返回 null。
 * 返回 null 时由上层转成「未配置」的非阻断提示，而不是抛异常打断对话。
 */
export function resolveDefaultSearchProvider(): SearchProvider | null {
  const config = loadSearchConfig();
  if (config === null || config.enabled === false) return null;
  try {
    return createSearchProvider(config);
  } catch {
    return null;
  }
}

/**
 * 默认 runner：取本地已配置 provider 执行检索。
 *
 * 未配置 provider 时以 `SearchError(code='config')` 拒绝，交由 `executeSearch` 归一化为
 * 「搜索失败」提示（不阻断对话）；取消语义完全沿用核心层（`code='aborted'`）。
 */
export const defaultSearchRunner: SearchRunner = (query, options) => {
  const provider = resolveDefaultSearchProvider();
  if (provider === null) {
    return Promise.reject(new SearchError('尚未配置联网搜索提供方', 'config'));
  }
  return provider.search({
    query,
    ...(options?.limit === undefined ? {} : { limit: options.limit }),
    ...(options?.signal === undefined ? {} : { signal: options.signal }),
  });
};
